'use client';

import { useEffect, useRef } from 'react';

interface IridescentShaderProps {
  /**
   * RGB color values (0.0 to 1.0 range)
   * @default [0.8, 0.4, 1.0] (purple/pink - Repetix)
   */
  color?: [number, number, number];

  /**
   * Animation speed multiplier
   * @default 0.6
   */
  speed?: number;

  /**
   * Mouse interaction amplitude
   * @default 0.12
   */
  amplitude?: number;

  /**
   * Additional CSS classes
   */
  className?: string;
}

export function IridescentShader({
  color = [0.8, 0.4, 1.0],
  speed = 0.6,
  amplitude = 0.12,
  className = '',
}: IridescentShaderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl');
    if (!gl) return;

    // Vertex shader
    const vertexShaderSource = `
      attribute vec2 aPosition;
      varying vec2 vUv;
      void main() {
        vUv = aPosition * 0.5 + 0.5;
        gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `;

    // Fragment shader with iridescent effect
    const fragmentShaderSource = `
      precision mediump float;
      uniform vec2 uResolution;
      uniform float uTime;
      uniform vec2 uMouse;
      uniform vec3 uColor;
      uniform float uAmplitude;
      uniform float uSpeed;
      varying vec2 vUv;

      void main() {
        float mr = min(uResolution.x, uResolution.y);
        vec2 uv = (vUv * 2.0 - 1.0) * uResolution.xy / mr;
        uv += (uMouse - vec2(0.5)) * uAmplitude;
        float d = -uTime * 0.5 * uSpeed;
        float a = 0.0;
        for (float i = 0.0; i < 8.0; ++i) {
          a += cos(i - d - a * uv.x);
          d += sin(uv.y * i + a);
        }
        d += uTime * 0.5 * uSpeed;
        vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
        col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
        gl_FragColor = vec4(col, 1.0);
      }
    `;

    // Compile shaders
    const vertexShader = gl.createShader(gl.VERTEX_SHADER)!;
    gl.shaderSource(vertexShader, vertexShaderSource);
    gl.compileShader(vertexShader);

    const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER)!;
    gl.shaderSource(fragmentShader, fragmentShaderSource);
    gl.compileShader(fragmentShader);

    // Create program
    const program = gl.createProgram()!;
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    gl.useProgram(program);

    // Set up geometry
    const positionBuffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
      gl.STATIC_DRAW
    );

    const aPositionLocation = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(aPositionLocation);
    gl.vertexAttribPointer(aPositionLocation, 2, gl.FLOAT, false, 0, 0);

    // Get uniform locations
    const uResolutionLocation = gl.getUniformLocation(program, 'uResolution');
    const uTimeLocation = gl.getUniformLocation(program, 'uTime');
    const uMouseLocation = gl.getUniformLocation(program, 'uMouse');
    const uColorLocation = gl.getUniformLocation(program, 'uColor');
    const uAmplitudeLocation = gl.getUniformLocation(program, 'uAmplitude');
    const uSpeedLocation = gl.getUniformLocation(program, 'uSpeed');

    // Set initial uniforms
    gl.uniform3f(uColorLocation, color[0], color[1], color[2]);
    gl.uniform2f(uMouseLocation, 0.5, 0.5);
    gl.uniform1f(uAmplitudeLocation, amplitude);
    gl.uniform1f(uSpeedLocation, speed);

    // Mouse tracking
    const mouse = { x: 0.5, y: 0.5 };
    const handleMouseMove = (e: MouseEvent) => {
      mouse.x = e.clientX / window.innerWidth;
      mouse.y = 1.0 - e.clientY / window.innerHeight;
    };
    window.addEventListener('mousemove', handleMouseMove);

    // Resize handler
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uResolutionLocation, canvas.width, canvas.height);
    };
    resize();
    window.addEventListener('resize', resize);

    // Animation loop
    let startTime = Date.now();
    let animationId: number;

    const render = () => {
      const time = (Date.now() - startTime) * 0.001;
      gl.uniform1f(uTimeLocation, time);
      gl.uniform2f(uMouseLocation, mouse.x, mouse.y);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      animationId = requestAnimationFrame(render);
    };
    render();

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, [color, speed, amplitude]);

  return (
    <canvas
      ref={canvasRef}
      className={`fixed inset-0 -z-10 ${className}`}
      style={{ width: '100vw', height: '100vh' }}
    />
  );
}
